import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import './CardPayment.css';

const CURRENCY_FLAGS = {
  BRL: '🇧🇷',
  USD: '🇺🇸',
  EUR: '🇪🇺',
  GBP: '🇬🇧',
  CAD: '🇨🇦',
  AUD: '🇦🇺',
  JPY: '🇯🇵',
};

const formatAmount = (amount, currency) => {
  const num = parseFloat(amount || 0);
  if (currency === 'JPY') return Math.round(num).toLocaleString();
  return num.toFixed(2).replace('.', currency === 'USD' || currency === 'GBP' ? '.' : ',');
};

const CardPayment = () => {
  const { token } = useParams();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [gateway, setGateway] = useState(null);

  // Stripe states
  const [stripe, setStripe] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [stripePublicKey, setStripePublicKey] = useState(null);
  const elementsRef = useRef(null);
  const cardElementRef = useRef(null);

  // MercadoPago states
  const [mpPublicKey, setMpPublicKey] = useState(null);
  const mpBrickControllerRef = useRef(null);

  // Currency states
  const [detectedCurrency, setDetectedCurrency] = useState(null);
  const [changingCurrency, setChangingCurrency] = useState(false);

  // --- Stripe flow ---
  const createPaymentIntent = useCallback(async () => {
    try {
      try {
        const configResponse = await api.get(`/payment/stripe-config?token=${token}`);
        if (configResponse.data.success && configResponse.data.public_key) {
          setStripePublicKey(configResponse.data.public_key);
        } else {
          throw new Error(configResponse.data.error || 'Chave publica nao retornada');
        }
      } catch (e) {
        const publicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY || '';
        if (publicKey) {
          setStripePublicKey(publicKey);
        } else {
          const errorMessage = e.response?.data?.error || e.message || 'Chave publica do Stripe nao configurada.';
          setError(errorMessage);
          return;
        }
      }

      const response = await api.post('/payment/card/create-intent', {
        token: token
      });

      if (response.data.success) {
        setClientSecret(response.data.client_secret);
        setPaymentIntentId(response.data.payment_intent_id);
      } else {
        throw new Error(response.data.error || 'Erro ao criar PaymentIntent');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao inicializar pagamento');
    }
  }, [token]);

  // --- MercadoPago flow ---
  const loadMercadoPagoConfig = useCallback(async () => {
    try {
      const response = await api.get(`/payment/mercadopago-config?token=${token}`);
      if (response.data.success && response.data.public_key) {
        setMpPublicKey(response.data.public_key);
      } else {
        setError(response.data.error || 'Chave publica do Mercado Pago nao configurada.');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao carregar configuracao do Mercado Pago.');
    }
  }, [token]);

  // Load transaction on mount
  useEffect(() => {
    if (token) {
      loadTransaction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Initialize Stripe when public key is available
  useEffect(() => {
    if (gateway === 'stripe' && stripePublicKey && window.Stripe) {
      const stripeInstance = window.Stripe(stripePublicKey);
      setStripe(stripeInstance);
    }
  }, [gateway, stripePublicKey]);

  // Create Stripe Elements when client_secret is ready
  useEffect(() => {
    if (gateway !== 'stripe') return;
    if (stripe && clientSecret && !elementsRef.current) {
      const elements = stripe.elements();
      elementsRef.current = elements;

      const cardElement = elements.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': {
              color: '#aab7c4',
            },
          },
          invalid: {
            color: '#9e2146',
          },
        },
      });

      cardElement.mount('#card-element');
      cardElementRef.current = cardElement;

      cardElement.on('change', ({ error: cardError }) => {
        if (cardError) {
          setError(cardError.message);
        } else {
          setError('');
        }
      });
    }

    return () => {
      if (cardElementRef.current) {
        cardElementRef.current.unmount();
      }
    };
  }, [gateway, stripe, clientSecret]);

  // Initialize MercadoPago Bricks when public key is available
  useEffect(() => {
    if (gateway !== 'mercadopago' || !mpPublicKey || !transaction) return;

    // Wait for SDK to be available
    if (!window.MercadoPago) {
      setError('SDK do Mercado Pago nao carregado. Recarregue a pagina.');
      return;
    }

    const initBrick = async () => {
      try {
        const mp = new window.MercadoPago(mpPublicKey, { locale: 'pt-BR' });
        const bricksBuilder = mp.bricks();

        // Destroy existing brick if any
        if (mpBrickControllerRef.current) {
          try {
            await mpBrickControllerRef.current.unmount();
          } catch {
            // ignore unmount errors
          }
          mpBrickControllerRef.current = null;
        }

        const controller = await bricksBuilder.create('cardPayment', 'mp-card-payment-brick', {
          initialization: {
            amount: parseFloat(transaction.amount),
          },
          customization: {
            visual: {
              style: {
                theme: 'default',
              },
            },
            paymentMethods: {
              maxInstallments: 12,
            },
          },
          callbacks: {
            onReady: () => {
              // Brick loaded
            },
            onSubmit: async (cardFormData) => {
              setProcessing(true);
              setError('');
              setSuccess('');

              try {
                const response = await api.post('/payment/card/process-mercadopago', {
                  token: token,
                  mp_token: cardFormData.token,
                  issuer_id: String(cardFormData.issuer_id),
                  payment_method_id: cardFormData.payment_method_id,
                  installments: cardFormData.installments,
                  payer_email: cardFormData.payer.email,
                  payer_identification_type: cardFormData.payer?.identification?.type || null,
                  payer_identification_number: cardFormData.payer?.identification?.number || null,
                });

                if (response.data.success) {
                  if (response.data.status === 'approved') {
                    setSuccess(response.data.message || 'Pagamento aprovado com sucesso!');
                    setTimeout(() => {
                      window.close();
                    }, 3000);
                  } else {
                    // pending / in_process
                    setSuccess(response.data.message || 'Pagamento em analise.');
                  }
                } else {
                  setError(response.data.error || 'Erro ao processar pagamento.');
                }
              } catch (err) {
                setError(err.response?.data?.error || err.message || 'Erro ao processar pagamento.');
              } finally {
                setProcessing(false);
              }
            },
            onError: (brickError) => {
              console.error('MP Brick error:', brickError);
              // Don't show internal brick errors to user unless critical
            },
          },
        });

        mpBrickControllerRef.current = controller;
      } catch (err) {
        console.error('Error initializing MP Brick:', err);
        setError('Erro ao inicializar formulario de pagamento do Mercado Pago.');
      }
    };

    initBrick();

    return () => {
      if (mpBrickControllerRef.current) {
        try {
          mpBrickControllerRef.current.unmount();
        } catch {
          // ignore
        }
        mpBrickControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateway, mpPublicKey, transaction?.amount]);

  const loadTransaction = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get(`/payment/transaction/${token}`);

      if (response.data.success) {
        const txn = response.data.transaction;
        setTransaction(txn);
        const txnGateway = txn.gateway || 'stripe';
        setGateway(txnGateway);

        // Detect currency by IP
        detectCurrency(txn);

        // Initialize the appropriate gateway
        if (txnGateway === 'stripe') {
          await createPaymentIntent();
        } else if (txnGateway === 'mercadopago') {
          await loadMercadoPagoConfig();
        }
      } else {
        throw new Error(response.data.error || 'Erro ao carregar transacao');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao carregar dados do pagamento');
    } finally {
      setLoading(false);
    }
  };

  const detectCurrency = async (txn) => {
    try {
      const response = await api.get('/payment/detect-currency');
      if (response.data.success && response.data.currency) {
        setDetectedCurrency(response.data.currency);
      }
    } catch {
      // Silently fail - default currency stays
    }
  };

  const handleCurrencyChange = async (newCurrency) => {
    if (!transaction || newCurrency === transaction.currency) return;

    try {
      setChangingCurrency(true);
      setError('');

      const response = await api.post(`/payment/transaction/${token}/change-currency`, {
        currency: newCurrency,
      });

      if (response.data.success) {
        setTransaction(response.data.transaction);

        if (gateway === 'stripe') {
          // Destroy existing Stripe Elements
          if (cardElementRef.current) {
            cardElementRef.current.unmount();
            cardElementRef.current = null;
          }
          elementsRef.current = null;
          setClientSecret(null);
          setPaymentIntentId(null);
          await createPaymentIntent();
        }
        // For MP: the useEffect watching transaction.amount will re-init the brick
      } else {
        setError(response.data.error || 'Erro ao trocar moeda');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao trocar moeda');
    } finally {
      setChangingCurrency(false);
    }
  };

  const handleStripeSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elementsRef.current || !cardElementRef.current) {
      setError('Stripe nao esta carregado. Por favor, recarregue a pagina.');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElementRef.current,
            billing_details: {
              name: document.getElementById('card-name')?.value || 'Cliente',
            },
          },
        }
      );

      if (confirmError) {
        setError(confirmError.message || 'Erro ao processar pagamento');
        setProcessing(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        try {
          const response = await api.post('/payment/card/confirm', {
            token: token,
            payment_intent_id: paymentIntent.id
          });

          if (response.data.success) {
            setSuccess(response.data.message || 'Pagamento processado com sucesso!');
            setTimeout(() => {
              window.close();
            }, 3000);
          } else {
            throw new Error(response.data.error || 'Erro ao confirmar pagamento no servidor');
          }
        } catch (err) {
          setError(err.response?.data?.error || err.message || 'Pagamento processado, mas houve erro ao atualizar status. Entre em contato com o suporte.');
        }
      } else if (paymentIntent.status === 'requires_action') {
        setError('Pagamento requer autenticacao adicional. Por favor, complete a autenticacao.');
      } else if (paymentIntent.status === 'requires_payment_method') {
        setError('Metodo de pagamento invalido. Por favor, verifique os dados do cartao.');
      } else if (paymentIntent.status === 'canceled') {
        setError('Pagamento cancelado.');
      } else {
        setError(`Status do pagamento: ${paymentIntent.status}. Entre em contato com o suporte.`);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao processar pagamento. Tente novamente.');
    } finally {
      setProcessing(false);
    }
  };

  // Build available currencies from plan data
  const getAvailableCurrencies = () => {
    if (!transaction?.payment_plan) return [];
    const plan = transaction.payment_plan;
    const currencies = new Set(['BRL']); // BRL always available

    if (plan.supported_currencies) {
      plan.supported_currencies.forEach(c => currencies.add(c));
    }

    if (plan.prices) {
      Object.keys(plan.prices).forEach(c => currencies.add(c.toUpperCase()));
    }

    return Array.from(currencies);
  };

  const availableCurrencies = transaction ? getAvailableCurrencies() : [];
  const showCurrencySelector = availableCurrencies.length > 1;

  if (loading) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Carregando dados do pagamento...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !transaction) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="error-message">
            <div className="error-icon">&#10060;</div>
            <h1>Erro no Pagamento</h1>
            <p>{error}</p>
            <button onClick={() => window.close()} className="btn-back">
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="error-message">
            <div className="error-icon">&#10060;</div>
            <h1>Transacao nao encontrada</h1>
            <p>Link de pagamento invalido ou expirado.</p>
            <button onClick={() => window.close()} className="btn-back">
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentCurrency = transaction.currency || 'BRL';
  const displayAmount = formatAmount(transaction.amount, currentCurrency);

  return (
    <div className="payment-page">
      <div className="payment-container">
        <div className="payment-header">
          <h1>Pagamento com Cartao</h1>
          <p>Preencha os dados do seu cartao de credito</p>
        </div>

        <div className="plan-info">
          <h2>{transaction.payment_plan?.title || 'Plano'}</h2>
          <div className="amount">{transaction.currency_symbol || 'R$'} {displayAmount}</div>
          <p>Pagamento unico {currentCurrency !== 'BRL' ? `(${currentCurrency})` : ''}</p>
        </div>

        {/* Currency selector - only shown when plan supports multiple currencies */}
        {showCurrencySelector && (
          <div className="currency-selector">
            <label>Moeda:</label>
            <div className="currency-buttons">
              {availableCurrencies.map(cur => (
                <button
                  key={cur}
                  type="button"
                  className={`currency-btn ${currentCurrency === cur ? 'active' : ''}`}
                  onClick={() => handleCurrencyChange(cur)}
                  disabled={changingCurrency || processing}
                >
                  {CURRENCY_FLAGS[cur] || ''} {cur}
                </button>
              ))}
            </div>
            {changingCurrency && <p className="currency-loading">Atualizando moeda...</p>}
            {detectedCurrency && detectedCurrency !== currentCurrency && availableCurrencies.includes(detectedCurrency) && (
              <p className="currency-suggestion">
                Detectamos que voce pode preferir pagar em {CURRENCY_FLAGS[detectedCurrency] || ''} {detectedCurrency}.{' '}
                <button
                  type="button"
                  className="currency-suggestion-link"
                  onClick={() => handleCurrencyChange(detectedCurrency)}
                  disabled={changingCurrency}
                >
                  Clique aqui para trocar
                </button>
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        {/* ===== STRIPE FORM ===== */}
        {gateway === 'stripe' && (
          <>
            {currentCurrency !== 'BRL' ? (
              /* Non-BRL: redirect to Stripe Checkout (supports SEPA, iDEAL, etc.) */
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
                  Voce sera redirecionado para a pagina segura do Stripe com os metodos de pagamento disponiveis para {currentCurrency}.
                </p>
                <button
                  className="btn-submit"
                  disabled={processing}
                  onClick={async () => {
                    try {
                      setProcessing(true);
                      setError('');
                      const response = await api.post('/payment/checkout/create-session', { token });
                      if (response.data.success && response.data.checkout_url) {
                        window.location.href = response.data.checkout_url;
                      } else {
                        setError(response.data.error || 'Erro ao criar sessao de checkout');
                      }
                    } catch (err) {
                      setError(err.response?.data?.error || 'Erro ao redirecionar para pagamento');
                    } finally {
                      setProcessing(false);
                    }
                  }}
                >
                  {processing ? 'Redirecionando...' : `Pagar ${transaction.currency_symbol || ''} ${displayAmount}`}
                </button>
                <div className="security-info">
                  Pagamento seguro processado pelo Stripe
                </div>
              </div>
            ) : (
              /* BRL: embedded Stripe Elements card form */
              <>
                {!stripePublicKey && (
                  <div className="alert alert-error">
                    Configuracao do Stripe nao encontrada. Entre em contato com o suporte.
                  </div>
                )}

                <form onSubmit={handleStripeSubmit} id="payment-form">
                  <div className="form-group">
                    <label htmlFor="card-name">Nome no Cartao</label>
                    <input
                      type="text"
                      id="card-name"
                      name="card-name"
                      placeholder="NOME COMO ESTA NO CARTAO"
                      required
                      disabled={processing || !stripe}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="card-element">Dados do Cartao</label>
                    <div id="card-element" style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                      {/* Stripe Elements sera montado aqui */}
                    </div>
                    <div id="card-errors" role="alert" style={{ color: '#dc3545', marginTop: '8px' }}></div>
                  </div>

                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={processing || !stripe || !clientSecret || changingCurrency}
                  >
                    {processing ? 'Processando...' : `Pagar ${transaction.currency_symbol || 'R$'} ${displayAmount}`}
                  </button>
                </form>

                <div className="security-info">
                  Seus dados estao seguros e criptografados pelo Stripe
                </div>
              </>
            )}
          </>
        )}

        {/* ===== MERCADOPAGO BRICKS ===== */}
        {gateway === 'mercadopago' && (
          <>
            {!mpPublicKey && !error && (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Carregando formulario de pagamento...</p>
              </div>
            )}

            <div id="mp-card-payment-brick" />

            {processing && (
              <div className="loading-spinner" style={{ marginTop: '16px' }}>
                <div className="spinner"></div>
                <p>Processando pagamento...</p>
              </div>
            )}

            <div className="security-info">
              Seus dados estao seguros e criptografados pelo Mercado Pago
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CardPayment;
